import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBlog } from "../../useHook";
import { useNavigate, useParams } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import { FiPlus } from "react-icons/fi";
import Swal from "sweetalert2";
import {
  AlignLeft,
  AlignRight,
  ArrowLeft,
  Eye,
  FileText,
  ImagePlus,
  Link2,
  Save,
  X,
} from "lucide-react";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { RichTextEditor } from "../../../../Components/Shared/RichTextEditor";
import {
  BLOG_STATUS_OPTIONS,
  urduFor,
} from "../../../../Utils/staticDropdownOptions";
import "../style.css";
import { applyDummyBlog } from "./dummyBlogContent";

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("blob:") || url.startsWith("data:")) return url;
  let normalized = url.replace(/\/api(?=\/)/, "");
  if (
    typeof window !== "undefined" &&
    window.location?.protocol === "https:" &&
    normalized.startsWith("http://")
  ) {
    normalized = "https:" + normalized.slice(5);
  }
  try {
    return encodeURI(decodeURI(normalized));
  } catch {
    return normalized;
  }
};

const getContentImageSrc = (image) => {
  if (!image) return null;
  if (typeof image === "string") return normalizeImageUrl(image);
  if (image.preview) return normalizeImageUrl(image.preview);
  return null;
};

const getExtensionFromMime = (mimeType = "") => {
  const subtype = mimeType.split("/")[1]?.split(";")[0]?.trim();
  if (!subtype) return "bin";
  return subtype === "jpeg" ? "jpg" : subtype;
};

const REQUIRED_IMAGE_WIDTH = 1600;
const REQUIRED_IMAGE_HEIGHT = 900;

const checkImageResolution = (file) => {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(
        img.width === REQUIRED_IMAGE_WIDTH &&
          img.height === REQUIRED_IMAGE_HEIGHT,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(false);
    };
    img.src = objectUrl;
  });
};

const revokePreviewUrl = (image) => {
  const src = image?.preview;
  if (typeof src === "string" && src.startsWith("blob:")) {
    URL.revokeObjectURL(src);
  }
};

const ignoreNextClick = () => {
  const ignore = (event) => {
    event.preventDefault();
    event.stopPropagation();
    document.removeEventListener("click", ignore, true);
  };
  document.addEventListener("click", ignore, true);
  window.setTimeout(() => document.removeEventListener("click", ignore, true), 400);
};

const emptyFormData = {
  categoryId: "",
  name: "",
  name_ur: "",
  status: "draft",
  status_ur: "مسودہ",
  description: "",
  description_ur: "",
  textContent_ur: "",
  headerImage: null,
  customUrl: "",
};

const emptyContentBlock = {
  text: "",
  text_ur: "",
  textAlignment: "left",
  image: null,
  imageAlignment: "right",
};

const cardIcon =
  "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]";
const alignWrap =
  "inline-flex rounded-lg border border-[#15173d]/10 bg-[#f3eef3] p-[3px]";
const alignBtn = (active) =>
  `inline-flex h-7 min-w-[4rem] items-center justify-center gap-1 rounded-md px-2 text-[11px] font-extrabold tracking-wide ${
    active ? "bg-white text-[#982598] shadow-sm" : "bg-transparent text-gray-500"
  }`;

function MediaPreview({
  src,
  alt,
  variant = "section",
  required = false,
  emptyTitle,
  emptyHint,
  onRemove,
  onOpen,
  onFileChange,
}) {
  const inputRef = useRef(null);
  const pickFile = () => inputRef.current?.click();

  return (
    <div className={`blog-media is-${variant}`}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*"
        onChange={onFileChange}
      />
      {src ? (
        <>
          <button
            type="button"
            className="blog-media-canvas"
            onClick={() => onOpen?.()}
          >
            <span className="blog-media-img-wrap">
              <img src={src} alt={alt} />
            </span>
            <span className="blog-media-zoom">
              <Eye size={13} />
              View
            </span>
          </button>
          <div className="blog-media-bar">
            <div className="blog-media-meta">
              <span className="blog-media-badge">1600 × 900</span>
              <span>Shown in full · no crop</span>
            </div>
            <div className="blog-media-actions">
              <button type="button" className="is-replace" onClick={pickFile}>
                Replace
              </button>
              {onRemove ? (
                <button type="button" className="is-remove" onClick={onRemove}>
                  Remove
                </button>
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <button
          type="button"
          className={`blog-media-drop ${required ? "is-required" : ""}`}
          onClick={pickFile}
        >
          <ImagePlus size={20} className="text-[#982598]" />
          <span className="text-[13px] font-extrabold text-[#15173d]">
            {emptyTitle}
          </span>
          <span className="text-[11px] text-gray-500">{emptyHint}</span>
          <span className="text-[10px] font-semibold text-[#982598]">
            {required ? "Required · 1600×900" : "Optional · 1600×900"}
          </span>
        </button>
      )}
    </div>
  );
}

export const BlogForm = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getBlogCategories, postBlog, updateBlog, getBlogById } = useBlog();

  const [blogCategories, setBlogCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [isLoadingBlog, setIsLoadingBlog] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [isFormValid, setIsFormValid] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([{ ...emptyContentBlock }]);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    if (!lightboxSrc) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setLightboxSrc(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxSrc]);

  useEffect(() => {
    const fetchBlogCategories = async () => {
      try {
        // limit=100 so the category dropdown gets the full list (not default page size of 10)
        const categories = await getBlogCategories(100);
        setBlogCategories(Array.isArray(categories) ? categories : []);
      } catch (error) {
        console.error("Error fetching blog categories:", error);
        setBlogCategories([]);
      }
    };
    fetchBlogCategories();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      setEditingBlogId(id);
      loadBlogForEdit(id);
    }
  }, [id, isEditMode]);

  const loadBlogForEdit = async (blogId) => {
    setIsLoadingBlog(true);
    try {
      const blogData = await getBlogById(blogId);

      setFormData({
        categoryId: blogData.categoryId || blogData.category?.id || "",
        name: blogData.name || "",
        name_ur: blogData.name_ur || "",
        status: blogData.status || "draft",
        status_ur:
          blogData.status_ur ||
          urduFor(BLOG_STATUS_OPTIONS, blogData.status || "draft"),
        description: blogData.description || "",
        description_ur: blogData.description_ur || "",
        textContent_ur: blogData.textContent_ur || "",
        headerImage: blogData.headerImage
          ? {
              preview: blogData.headerImage,
              file: null,
            }
          : null,
        customUrl: blogData.customUrl || "",
      });

      if (blogData.content && Array.isArray(blogData.content)) {
        const sections = [];
        let currentSection = { text: "", text_ur: "", image: null };

        blogData.content.forEach((block) => {
          if (block.type === "text") {
            if (currentSection.text !== "" || currentSection.image !== null) {
              sections.push(currentSection);
              currentSection = {
                text: block.content,
                text_ur: "",
                textAlignment: block.alignment || "left",
                image: null,
                imageAlignment: "right",
              };
            } else {
              currentSection.text = block.content;
              currentSection.textAlignment = block.alignment || "left";
            }
          } else if (block.type === "image") {
            currentSection.image = block.content;
            currentSection.imageAlignment = block.alignment || "right";
            sections.push(currentSection);
            currentSection = {
              text: "",
              text_ur: "",
              textAlignment: "left",
              image: null,
              imageAlignment: "right",
            };
          }
        });

        if (currentSection.text !== "" || currentSection.image !== null) {
          sections.push(currentSection);
        }

        if (blogData.content_ur && Array.isArray(blogData.content_ur)) {
          const urTexts = blogData.content_ur
            .filter((b) => b.type === "text")
            .map((b) => b.content || "");
          let urIdx = 0;
          sections.forEach((section) => {
            if (section.text && urIdx < urTexts.length) {
              section.text_ur = urTexts[urIdx++];
            }
          });
        }

        setContentBlocks(
          sections.length > 0 ? sections : [{ ...emptyContentBlock }],
        );
      } else {
        setContentBlocks([{ ...emptyContentBlock }]);
      }
    } catch (error) {
      console.error("Error loading blog for edit:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load blog data for editing.",
        icon: "error",
      });
      navigate("/blogs");
    } finally {
      setIsLoadingBlog(false);
    }
  };

  const addContentBlock = (index, e) => {
    e.preventDefault();
    const newBlocks = [...contentBlocks];
    newBlocks.splice(index + 1, 0, { ...emptyContentBlock });
    setContentBlocks(newBlocks);
  };

  const removeBlock = (index, e) => {
    e.preventDefault();
    if (contentBlocks.length > 1) {
      setContentBlocks((prev) => {
        revokePreviewUrl(prev[index]?.image);
        return prev.filter((_, i) => i !== index);
      });
    }
  };

  const handleTextChange = useCallback((index, value) => {
    setContentBlocks((prev) => {
      if (!prev[index] || prev[index].text === value) return prev;
      const next = [...prev];
      next[index] = { ...next[index], text: value };
      return next;
    });
  }, []);

  const handleUrduTextChange = useCallback((index, value) => {
    setContentBlocks((prev) => {
      if (!prev[index] || prev[index].text_ur === value) return prev;
      const next = [...prev];
      next[index] = { ...next[index], text_ur: value };
      return next;
    });
  }, []);

  const handleAlignmentChange = (index, field, value) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index][field] = value;
    setContentBlocks(newBlocks);
  };

  const handleImageUpload = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    ignoreNextClick();

    const isValid = await checkImageResolution(file);
    if (!isValid) {
      Swal.fire({
        title: "Invalid Image Resolution",
        text: `Image "${file.name}" does not have the required resolution of 1600x900. Please upload the correct resolution.`,
        icon: "error",
      });
      return;
    }

    const preview = URL.createObjectURL(file);
    setContentBlocks((prev) =>
      prev.map((block, i) => {
        if (i !== index) return block;
        revokePreviewUrl(block.image);
        return {
          ...block,
          image: { preview, file },
        };
      }),
    );
  };

  const handleHeaderImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    ignoreNextClick();

    const isValid = await checkImageResolution(file);
    if (!isValid) {
      Swal.fire({
        title: "Invalid Image Resolution",
        text: `Image "${file.name}" does not have the required resolution of 1600x900. Please upload the correct resolution.`,
        icon: "error",
      });
      return;
    }

    const preview = URL.createObjectURL(file);
    setFormData((prev) => {
      revokePreviewUrl(prev.headerImage);
      return {
        ...prev,
        headerImage: { preview, file },
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "status") {
        next.status_ur = urduFor(BLOG_STATUS_OPTIONS, value);
      }
      return next;
    });
  };

  useEffect(() => {
    const validateForm = () => {
      const isHeaderImageSelected =
        formData.headerImage !== null &&
        (formData.headerImage.file ||
          formData.headerImage.preview ||
          typeof formData.headerImage === "string");

      const isCategorySelected = formData.categoryId !== "";
      const isNameFilled = formData.name.trim() !== "";
      const isDescriptionFilled = formData.description.trim() !== "";
      const isCustomUrlFilled = formData.customUrl.trim() !== "";

      const isValid =
        isHeaderImageSelected &&
        isCategorySelected &&
        isNameFilled &&
        isDescriptionFilled &&
        isCustomUrlFilled;

      setIsFormValid(isValid);
    };

    validateForm();
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Ignore bubbled submits from portaled dialogs / nested controls.
    if (e.target !== e.currentTarget) return;

    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();

      formDataObj.append("name", formData.name);
      if (formData.name_ur) formDataObj.append("name_ur", formData.name_ur);
      formDataObj.append("description", formData.description);
      if (formData.description_ur)
        formDataObj.append("description_ur", formData.description_ur);
      if (formData.textContent_ur)
        formDataObj.append("textContent_ur", formData.textContent_ur);
      formDataObj.append("status", formData.status);
      formDataObj.append("status_ur", formData.status_ur || "");
      formDataObj.append("categoryId", formData.categoryId);
      formDataObj.append("customUrl", formData.customUrl);
      if (formData.headerImage?.file) {
        formDataObj.append("headerImage", formData.headerImage.file);
      }

      const contentForJson = [];
      const contentUrForJson = [];

      for (let index = 0; index < contentBlocks.length; index++) {
        const section = contentBlocks[index];

        contentForJson.push({
          type: "text",
          content: section.text,
          alignment: section.textAlignment || "left",
        });
        // Always emit a UR text slot so section indices stay aligned with EN.
        contentUrForJson.push({
          type: "text",
          content: section.text_ur || "",
          alignment: section.textAlignment || "left",
        });

        if (section.image) {
          const imageIndex = contentForJson.length;
          const urImageIndex = contentUrForJson.length;
          const imageBlock = {
            type: "image",
            content: "",
            alignment: section.imageAlignment || "right",
          };

          const imageFile =
            section.image instanceof File
              ? section.image
              : section.image?.file instanceof File
                ? section.image.file
                : null;

          if (imageFile) {
            formDataObj.append(`content[${imageIndex}][content]`, imageFile);
            formDataObj.append(
              `content_ur[${urImageIndex}][content]`,
              imageFile,
            );
            imageBlock.content = "";
          } else if (
            typeof section.image === "string" &&
            section.image.startsWith("data:")
          ) {
            const response = await fetch(section.image);
            const blob = await response.blob();
            const mimeType = blob.type || "application/octet-stream";
            const ext = getExtensionFromMime(mimeType);
            const file = new File([blob], `image-${index}.${ext}`, {
              type: mimeType,
            });
            formDataObj.append(`content[${imageIndex}][content]`, file);
            formDataObj.append(`content_ur[${urImageIndex}][content]`, file);
            imageBlock.content = "";
          } else if (typeof section.image === "string") {
            imageBlock.content = section.image;
          } else if (
            typeof section.image?.preview === "string" &&
            !section.image.file
          ) {
            imageBlock.content = section.image.preview;
          }
          // Shared section image for both locales (same upload / URL).
          contentForJson.push(imageBlock);
          contentUrForJson.push({ ...imageBlock });
        }
      }

      formDataObj.append("content", JSON.stringify(contentForJson));
      formDataObj.append("content_ur", JSON.stringify(contentUrForJson));

      if (isEditMode) {
        await updateBlog(editingBlogId, formDataObj);
      } else {
        await postBlog(formDataObj);
      }

      resetForm();

      Swal.fire({
        title: "Success!",
        text: isEditMode
          ? "Blog has been updated successfully."
          : "Blog has been created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate("/blogs");
    } catch (error) {
      console.error("Error saving blog:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      Swal.fire({
        title: "Error!",
        text: isEditMode
          ? "Failed to update blog. Please try again."
          : "Failed to create blog. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData((prev) => {
      revokePreviewUrl(prev.headerImage);
      return { ...emptyFormData };
    });
    setContentBlocks((prev) => {
      prev.forEach((block) => revokePreviewUrl(block.image));
      return [{ ...emptyContentBlock }];
    });
    setEditingBlogId(null);
    setIsFormValid(false);
  };

  const handleCancel = () => {
    resetForm();
    navigate("/blogs");
  };

  const renderBlock = (section, index) => {
    return (
      <div
        key={index}
        className="rounded-xl border border-[#15173d]/10 bg-white"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#15173d]/8 bg-[#faf8fa] px-4 py-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#15173d] text-[11px] font-extrabold text-white">{index + 1}</span>
            <span className="text-sm font-bold text-[#15173d] truncate">
              Section {index + 1}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => addContentBlock(index, e)}
              className="ui-btn-back !h-8 !w-8 !rounded-lg"
              title="Add Section Below"
            >
              <FiPlus className="w-4 h-4" />
            </button>
            {contentBlocks.length > 1 && (
              <button
                type="button"
                onClick={(e) => removeBlock(index, e)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                title="Remove Section"
              >
                <MdDelete className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <label className="ui-form-label !mb-0">Text Content</label>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                Alignment
              </span>
              <div className={alignWrap}>
                <button
                  type="button"
                  className={alignBtn(section.textAlignment === "left")}
                  onClick={() =>
                    handleAlignmentChange(index, "textAlignment", "left")
                  }
                >
                  <AlignLeft size={14} />
                  Left
                </button>
                <button
                  type="button"
                  className={alignBtn(section.textAlignment === "right")}
                  onClick={() =>
                    handleAlignmentChange(index, "textAlignment", "right")
                  }
                >
                  <AlignRight size={14} />
                  Right
                </button>
              </div>
            </div>
          </div>
          <RichTextEditor
            value={section.text}
            onChange={(value) => handleTextChange(index, value)}
            placeholder="Write your section content here..."
          />
          <div>
            <label
              className="mb-1.5 block text-right text-sm font-bold text-brand-dark"
              dir="rtl"
            >
              مواد
            </label>
            <RichTextEditor
              dir="rtl"
              value={section.text_ur || ""}
              onChange={(value) => handleUrduTextChange(index, value)}
              placeholder="اردو میں لکھیں..."
            />
          </div>

          <div className="space-y-3 pt-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="ui-form-label !mb-0">Section image</label>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  Alignment
                </span>
                <div className={alignWrap}>
                  <button
                    type="button"
                    className={alignBtn(section.imageAlignment === "left")}
                    onClick={() =>
                      handleAlignmentChange(index, "imageAlignment", "left")
                    }
                  >
                    <AlignLeft size={14} />
                    Left
                  </button>
                  <button
                    type="button"
                    className={alignBtn(section.imageAlignment === "right")}
                    onClick={() =>
                      handleAlignmentChange(index, "imageAlignment", "right")
                    }
                  >
                    <AlignRight size={14} />
                    Right
                  </button>
                </div>
              </div>
            </div>
            <MediaPreview
              src={getContentImageSrc(section.image)}
              alt={`Section ${index + 1} image`}
              variant="section"
              emptyTitle="Upload section image"
              emptyHint="Full 1600×900 preview after upload"
              onFileChange={(e) => handleImageUpload(index, e)}
              onOpen={() => {
                const src = getContentImageSrc(section.image);
                if (src) setLightboxSrc(src);
              }}
              onRemove={() => {
                setContentBlocks((prev) =>
                  prev.map((block, i) => {
                    if (i !== index) return block;
                    revokePreviewUrl(block.image);
                    return { ...block, image: null };
                  }),
                );
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  if (isLoadingBlog) {
    return (
      <div className="blog-form-page flex min-h-[240px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[#982598]/20 border-t-[#982598]" />
      </div>
    );
  }

  const headerSrc = formData.headerImage
    ? normalizeImageUrl(
        formData.headerImage.preview
          ? formData.headerImage.preview
          : formData.headerImage,
      )
    : null;

  return (
    <div className="blog-form-page">
      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="ui-btn-back"
              aria-label="Back"
              disabled={isSubmitting}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <p className="mb-0 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#982598]">
                Blogs
              </p>
              <h2 className="text-xl font-extrabold tracking-tight text-[#15173d]">
                {isEditMode ? "Edit Blog" : "Create Blog"}
              </h2>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#15173d]/10 bg-white px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-[#15173d]">
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  formData.status === "published"
                    ? "bg-green-600"
                    : formData.status === "archived"
                      ? "bg-gray-400"
                      : "bg-[#982598]"
                }`}
              />
              {formData.status || "draft"}
            </span>
            {/* <button
              type="button"
              onClick={() =>
                applyDummyBlog(
                  setFormData,
                  setContentBlocks,
                  blogCategories[0]?.id || "",
                )
              }
              className="rounded-lg border border-dashed border-[#982598]/40 bg-white px-3.5 py-2 text-sm font-semibold text-[#982598] hover:bg-[#982598]/5"
            >
              Load dummy blog
            </button> */}
            <button
              type="button"
              onClick={handleCancel}
              className="ui-btn-cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ui-btn-save"
              disabled={isSubmitting || !isFormValid}
            >
              <Save size={14} />
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </div>

        {Array.isArray(blogCategories) && blogCategories.length === 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
            <span className="text-sm font-semibold">Setup Required</span>
            <ul className="mt-1 ml-4 list-disc text-sm text-amber-800">
              <li>No categories available. Please add blog categories first.</li>
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-4">
            <section className="ui-card">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className={cardIcon}>
                    <ImagePlus size={15} />
                  </span>
                  <div>
                    <h3 className="text-[14px] font-extrabold text-[#15173d]">
                      Cover image
                    </h3>
                    <p className="text-[11px] text-gray-500">
                      Full 1600×900 preview · PNG, JPG, WEBP, AVIF
                    </p>
                  </div>
                </div>
              </div>
              <MediaPreview
                src={headerSrc}
                alt="Blog cover preview"
                variant="cover"
                required
                emptyTitle="Upload cover image"
                emptyHint="Drop or click to upload"
                onFileChange={handleHeaderImageUpload}
                onOpen={() => headerSrc && setLightboxSrc(headerSrc)}
                onRemove={() =>
                  setFormData((prev) => {
                    revokePreviewUrl(prev.headerImage);
                    return { ...prev, headerImage: null };
                  })
                }
              />
            </section>

            <section className="ui-card">
              <div className="mb-4 flex items-center gap-2.5">
                <span className={cardIcon}>
                  <FileText size={15} />
                </span>
                <div>
                  <h3 className="text-[14px] font-extrabold text-[#15173d]">
                    Article details
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    Title, category and public URL
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="ui-form-label">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter blog name"
                    className="ui-input-field"
                  />
                  <UrduInput
                    label="نام"
                    name="name_ur"
                    value={formData.name_ur}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label className="ui-form-label">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    className="ui-select-field"
                  >
                    <option value="">Select a category</option>
                    {Array.isArray(blogCategories) &&
                      blogCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="ui-form-label">
                    Custom URL <span className="text-red-500">*</span>
                  </label>
                  <div className="flex overflow-hidden rounded-lg border border-gray-200 bg-white focus-within:border-[#982598]">
                    <span className="flex items-center gap-1 whitespace-nowrap border-r border-gray-200 bg-[#f8f5f8] px-2.5 text-[11px] font-bold text-gray-500">
                      <Link2 size={12} />
                      /blog/
                    </span>
                    <input
                      type="text"
                      name="customUrl"
                      value={formData.customUrl}
                      onChange={handleInputChange}
                      placeholder="article-slug"
                      className="ui-input-field rounded-none border-0"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="ui-card">
              <div className="mb-4 flex items-center gap-2.5">
                <span className={cardIcon}>
                  <FileText size={15} />
                </span>
                <div>
                  <h3 className="text-[14px] font-extrabold text-[#15173d]">
                    Description
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    Short summary shown in listings
                  </p>
                </div>
              </div>
              <div>
                <label className="ui-form-label">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief summary of the blog"
                  className="ui-textarea-field min-h-[88px]"
                />
                <UrduInput
                  label="تفصیل"
                  name="description_ur"
                  value={formData.description_ur}
                  onChange={handleInputChange}
                  type="textarea"
                  rows={3}
                />
                {/* متنی مواد — hidden for now
                <div className="mt-2">
                  <label
                    className="mb-1.5 block text-right text-sm font-bold text-brand-dark"
                    dir="rtl"
                  >
                    متنی مواد
                  </label>
                  <RichTextEditor
                    dir="rtl"
                    minHeight={140}
                    value={formData.textContent_ur}
                    onChange={(html) =>
                      setFormData((prev) => ({ ...prev, textContent_ur: html }))
                    }
                    placeholder="اردو میں لکھیں..."
                  />
                </div>
                */}
              </div>
            </section>

            <section className="ui-card">
              <div className="mb-4 flex items-center gap-2.5">
                <span className={cardIcon}>
                  <ImagePlus size={15} />
                </span>
                <div>
                  <h3 className="text-[14px] font-extrabold text-[#15173d]">
                    Content sections
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    Text plus an optional full image
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                {contentBlocks.map((block, index) =>
                  renderBlock(block, index),
                )}
                <button
                  type="button"
                  onClick={(e) =>
                    addContentBlock(contentBlocks.length - 1, e)
                  }
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[#e491c9] bg-[rgba(152,37,152,0.03)] py-2.5 text-sm font-bold text-[#982598] transition-colors hover:bg-[rgba(152,37,152,0.07)]"
                >
                  <FiPlus className="h-4 w-4" />
                  Add section
                </button>
              </div>
            </section>
          </div>

          <aside className="xl:sticky xl:top-4">
            <div className="ui-card">
              <p className="mb-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-gray-500">
                Publish status
              </p>
              <div className="mb-3 grid grid-cols-3 gap-1">
                {BLOG_STATUS_OPTIONS.map((o) => (
                  <button
                    key={o.en}
                    type="button"
                    className={`rounded-lg border py-1.5 text-[10px] font-extrabold uppercase tracking-wide ${
                      formData.status === o.en
                        ? "border-[#982598] bg-[#982598] text-white"
                        : "border-[#15173d]/10 bg-white text-gray-500 hover:border-[#982598]/40"
                    }`}
                    onClick={() =>
                      handleInputChange({
                        target: { name: "status", value: o.en },
                      })
                    }
                  >
                    {o.enLabel}
                  </button>
                ))}
              </div>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
              >
                {BLOG_STATUS_OPTIONS.map((o) => (
                  <option key={o.en} value={o.en}>
                    {o.enLabel}
                  </option>
                ))}
              </select>
              <label
                className="ui-form-label text-right"
                dir="rtl"
              >
                حالت
              </label>
              <select
                name="status_ur"
                value={formData.status_ur}
                onChange={handleInputChange}
                dir="rtl"
                className="ui-select-field text-right"
              >
                <option value="">منتخب کریں...</option>
                {BLOG_STATUS_OPTIONS.map((o) => (
                  <option key={o.ur} value={o.ur}>
                    {o.ur}
                  </option>
                ))}
              </select>
            </div>
          </aside>
        </div>
      </form>

      {lightboxSrc ? (
        <div
          className="blog-lightbox"
          onClick={() => setLightboxSrc(null)}
          role="presentation"
        >
          <button
            type="button"
            className="blog-lightbox-close"
            onClick={() => setLightboxSrc(null)}
            aria-label="Close image"
          >
            <X size={16} />
          </button>
          <img
            src={lightboxSrc}
            alt="Full size preview"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
};

export default BlogForm;
